import React from 'react'
import {Button, Link as ChakraLink, useBreakpointValue} from '@chakra-ui/react'
import {Link as RouterLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {getGDIHideStyles, mergeGDISx, resolveGDIStyles} from '../../utils/gdi-styles'
import {useGetLinkData} from '../../../../../contexts/category-url-context'
import {resolveGDILink} from '../../utils/gdi-link-resolver'
import {useIsInsideGDILink} from '../../utils/gdi-link-context'
import {getExpandedFromContentArea} from '../../utils/gdi-content-area'
import {parseShortcodes} from '../../utils/gdi-shortcode-parser'
import {useIsAudienceTargetMatch} from '../../utils/gdi-audience-targeting'
import {useBrand} from '../../../../../contexts/brand-context'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    BUTTON_LAYOUT_DEFAULTS,
    CTA_STYLE_MAP,
    FOCUS_VISIBLE,
    buildCtaStyleProps,
    extraStylePropsFor,
    getCtaInteractionProps,
    getCtaStyleNameByBreakpoint,
    getCtaStylesForResolve,
    getCtaTextByBreakpoint,
    getCtaVariant,
    resolveFontStyleProps,
    resolveResponsiveFontStyleProps
} from '../../utils/gdi-cta-style'
import ProductsPopover from './products-popover'
import GDICustomDrawer from '../gdiDrawerBlock'

const GDICtaBlock = ({
    HideBlockOn,
    GDICtaBlockLink,
    GDICtaBlockStylesPerViewType,
    GDICtaBlockAudienceTargeting,
    ...props
}) => {
    const audienceMatches = useIsAudienceTargetMatch(GDICtaBlockAudienceTargeting)
    const hideStyles = getGDIHideStyles(HideBlockOn)
    if (hideStyles.doNotLoadComponent) return null
    if (!audienceMatches) return null

    const getLinkData = useGetLinkData()
    const isInsideLink = useIsInsideGDILink()
    // GDICtaBlockLink is a ContentArea array — extract the first expanded GDILinkBlock
    const linkBlock = getExpandedFromContentArea(GDICtaBlockLink)
    /* When an ancestor (e.g. GDIContentTileBlock) already wraps this subtree in a
     * link, render as a plain button to avoid nested `<a>` elements. The CTA's own
     * link is dropped in favor of the ancestor's. */
    const linkData = isInsideLink ? null : resolveGDILink(linkBlock, getLinkData)

    /* When the linked GDILinkBlock has LinkType 'Popover Product IDs' (comma-separated
     * LinkReference), the CTA opens a popover (desktop) or drawer (mobile) listing
     * the individual products instead of navigating to a combined product set URL. */
    const multiProductIds =
        !isInsideLink && linkBlock?.LinkType === 'Popover Product IDs' && typeof linkBlock?.LinkReference === 'string'
            ? linkBlock.LinkReference.split(',')
                  .map((id) => id.trim())
                  .filter(Boolean)
            : []
    const isMultiProductCta = multiProductIds.length > 0

    const {desktopHoverEntry, stylesForResolve} = getCtaStylesForResolve(
        GDICtaBlockStylesPerViewType
    )
    const {styles: responsiveStyles, hoverStyles} = resolveGDIStyles(stylesForResolve, CTA_STYLE_MAP)

    const {styles: fontStyleStyles, hoverStyles: fontStyleHoverStyles} = resolveGDIStyles(
        stylesForResolve,
        {FontStyle: 'fontStyle'}
    )
    Object.assign(responsiveStyles, resolveResponsiveFontStyleProps(fontStyleStyles.fontStyle))
    Object.assign(hoverStyles, resolveFontStyleProps(fontStyleHoverStyles.fontStyle))
    const ctaTextByBreakpoint = getCtaTextByBreakpoint(GDICtaBlockStylesPerViewType)
    const styleNameByBreakpoint = getCtaStyleNameByBreakpoint(GDICtaBlockStylesPerViewType)
    const ctaText = useBreakpointValue(ctaTextByBreakpoint) ?? ctaTextByBreakpoint.base
    const styleName = useBreakpointValue(styleNameByBreakpoint) ?? styleNameByBreakpoint.base

    const {directStyles, sxStyles} = buildCtaStyleProps(
        responsiveStyles,
        desktopHoverEntry,
        styleName
    )
    const variant = getCtaVariant(styleName)
    const extraStyleProps = extraStylePropsFor(styleName)
    const {hoverProp, extraSpread} = getCtaInteractionProps(extraStyleProps, hoverStyles)
    const buttonText = ctaText || linkBlock?.AriaLabel || linkBlock?.Name || ''
    /* Inline CMS directives in CtaText — same shortcode syntax used by GDITextBlock,
     * e.g. `Shop {fontColor: '#fff', text: 'now'}` or `{brandName: true}`. We only
     * pay the parse cost (and use dangerouslySetInnerHTML) when the text actually
     * contains a shortcode opener; otherwise render as a plain string. Hydration-
     * dependent directives (tooltip, readMore) are not supported here — they
     * require effects and aren't meaningful inside a button label. */
    const {brandId} = useBrand()
    const {data: customer} = useCurrentCustomer()
    const isRegistered = customer?.isRegistered ?? false
    const brandName = brandId ? brandId.charAt(0).toUpperCase() + brandId.slice(1) : undefined
    const hasShortcode = typeof buttonText === 'string' && buttonText.includes('{')
    const buttonTextHtml = hasShortcode
        ? parseShortcodes(buttonText, {brandName, isRegistered})
        : null
    /* Resolve any {fontSize: <token>} directives the parser tagged with
     * data-gdi-textstyle into actual Chakra textStyle rules (same trick used by
     * GDITextBlock). Without this, design-system tokens like `webH1` render
     * inert because the attribute alone applies no styles. */
    const textStyleRules = (() => {
        if (!buttonTextHtml) return null
        const rules = {}
        const re = /data-gdi-textstyle="([^"]+)"/g
        let m
        while ((m = re.exec(buttonTextHtml)) !== null) {
            rules[`& [data-gdi-textstyle="${m[1]}"]`] = {textStyle: m[1]}
        }
        return Object.keys(rules).length > 0 ? rules : null
    })()
    const buttonChildren = hasShortcode ? (
        <span dangerouslySetInnerHTML={{__html: buttonTextHtml}} />
    ) : (
        buttonText
    )
    const ariaLabel = linkBlock?.AriaLabel || undefined
    const buttonProps = {
        'aria-label': ariaLabel,
        'data-block-type': 'GDICtaBlock',
        variant,
        _hover: hoverProp,
        _focusVisible: FOCUS_VISIBLE,
        ...BUTTON_LAYOUT_DEFAULTS,
        ...extraSpread,
        ...directStyles,
        sx: mergeGDISx(sxStyles, hideStyles.sx, textStyleRules),
        ...props
    }

    /* Multi-product CTA: render a plain button (no anchor) wrapped in the
     * popover/drawer trigger. Must be checked before the !linkData branch below
     * because resolveGDILink returns null for 'Popover Product IDs' (it is not
     * a navigable link), which would otherwise cause an early plain-button return. */
    if (isMultiProductCta) {
        const triggerButton = (
            <Button {...buttonProps}>
                {buttonChildren}
            </Button>
        )
        return <ProductsPopover productIds={multiProductIds} trigger={triggerButton} />
    }

    if (!linkData) {
        /* Custom Drawer: open a WebDrawer with CMS-authored GDIDrawerBlock content. */
        if (linkBlock?.LinkType === 'Custom Drawer' && !isInsideLink) {
            const customDrawerBlock = getExpandedFromContentArea(linkBlock?.CustomDrawer)
            if (customDrawerBlock) {
                return (
                    <GDICustomDrawer
                        drawerBlock={customDrawerBlock}
                        renderTrigger={({onOpen}) => (
                            <Button onClick={onOpen} {...buttonProps}>
                                {buttonChildren}
                            </Button>
                        )}
                    />
                )
            }
        }

        /* System Drawer ID: dispatch gdi:openDrawer so the relevant modal/drawer opens.
         * Supported values: login, register, mini-cart, search. */
        const drawerOnClick =
            linkBlock?.LinkType === 'System Drawer ID' && linkBlock?.LinkReference
                ? (e) => {
                      e.preventDefault()
                      if (typeof window !== 'undefined') {
                          window.dispatchEvent(
                              new CustomEvent('gdi:openDrawer', {
                                  detail: {drawer: linkBlock.LinkReference}
                              })
                          )
                      }
                  }
                : undefined

        return (
            <Button onClick={drawerOnClick} {...buttonProps}>
                {buttonChildren}
            </Button>
        )
    }

    const {href, isExternal, target, rel} = linkData

    if (isExternal) {
        return (
            <Button as={ChakraLink} href={href} target={target} rel={rel} {...buttonProps}>
                {buttonChildren}
            </Button>
        )
    }

    return (
        <Button as={RouterLink} to={href} {...buttonProps}>
            {buttonChildren}
        </Button>
    )
}

GDICtaBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    GDICtaBlockLink: PropTypes.array,
    GDICtaBlockStylesPerViewType: PropTypes.array,
    GDICtaBlockAudienceTargeting: PropTypes.object
}

export default GDICtaBlock
